import React, { useEffect, useState, useRef } from 'react';
import { authenticatedFetchJSON } from '../../utils/authenticatedFetch';
import * as d3 from 'd3';
import { motion, AnimatePresence } from 'framer-motion';
import './TreeVisualizer.css';

interface NodeDetails {
    [key: string]: any;
}

interface TreeNode {
    name: string;
    type: 'root' | 'branch' | 'epoch' | 'agent_group' | 'interaction' | 'fmea_item';
    children?: TreeNode[];
    details?: NodeDetails;
    timestamp?: string;
}

const TreeVisualizer: React.FC = () => {
    const [data, setData] = useState<TreeNode | null>(null);
    const [selectedNode, setSelectedNode] = useState<TreeNode | null>(null);
    const svgRef = useRef<SVGSVGElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [loading, setLoading] = useState(true);

    const [availableFiles, setAvailableFiles] = useState<string[]>([]);
    const [selectedFile, setSelectedFile] = useState<string>('completed_epochs.json');

    useEffect(() => {
        const fetchFiles = async () => {
            try {
                const files = await authenticatedFetchJSON<string[]>('/api/visualization/epoch-files');
                setAvailableFiles(files);
                if (files.length > 0 && !files.includes('completed_epochs.json')) {
                    // If default not found but others are, pick first
                    setSelectedFile(files[0]);
                }
            } catch (err) {
                console.error("Failed to load epoch files:", err);
                // Fallback to empty list or show error
            }
        };

        fetchFiles();
    }, []);

    useEffect(() => {
        const fetchTree = async () => {
            if (!selectedFile) return;

            try {
                setLoading(true);
                const data = await authenticatedFetchJSON<TreeNode>(`/api/visualization/tree-structure?filename=${encodeURIComponent(selectedFile)}`);
                setData(data);
                setLoading(false);
            } catch (err) {
                console.error("Failed to load tree data", err);
                setLoading(false);
            }
        };

        fetchTree();
    }, [selectedFile]);

    useEffect(() => {
        if (!data || !svgRef.current || !containerRef.current) return;

        const width = containerRef.current.clientWidth;
        const height = Math.max(800, containerRef.current.clientHeight);
        const margin = { top: 60, right: 120, bottom: 20, left: 120 }; // Increased top margin for UI controls

        // Clear previous
        d3.select(svgRef.current).selectAll("*").remove();

        const svg = d3.select(svgRef.current)
            .attr("width", width)
            .attr("height", height)
            .append("g")
            .attr("transform", `translate(${margin.left},${margin.top})`);

        // Create hierarchy
        const root = d3.hierarchy<TreeNode>(data);

        // Tree layout
        const treeLayout = d3.tree<TreeNode>().size([height - margin.top - margin.bottom, width - margin.left - margin.right]);
        treeLayout(root);

        // Links
        svg.selectAll(".link")
            .data(root.links())
            .enter().append("path")
            .attr("class", "link")
            .attr("d", d3.linkHorizontal<d3.HierarchyPointLink<TreeNode>, d3.HierarchyPointNode<TreeNode>>()
                .x(d => d.y)
                .y(d => d.x) as any
            );

        // Nodes
        const nodes = svg.selectAll(".node")
            .data(root.descendants())
            .enter().append("g")
            .attr("class", d => `node ${d.data.type}`)
            .attr("transform", d => `translate(${d.y},${d.x})`)
            .on("click", (event, d) => {
                setSelectedNode(d.data);
            });

        nodes.append("circle")
            .attr("r", 8);

        nodes.append("text")
            .attr("dy", ".35em")
            .attr("x", d => d.children ? -13 : 13)
            .style("text-anchor", d => d.children ? "end" : "start")
            .text(d => d.data.name);

        // Zoom behavior
        const zoom = d3.zoom<SVGSVGElement, unknown>()
            .scaleExtent([0.5, 3])
            .on("zoom", (event) => {
                svg.attr("transform", event.transform);
            });

        d3.select(svgRef.current).call(zoom);

    }, [data]);

    return (
        <div className="visualizer-container">
            <header className="visualizer-header">
                <div className="flex justify-between items-center w-full">
                    <div>
                        <h1>Magnus Insight: Evolutionary Tree</h1>
                        <p>Visualizing FMEA Growth, Agents, and Interactions</p>
                    </div>
                    <div className="flex items-center gap-2 bg-card/10 p-2 rounded">
                        <span className="text-sm font-medium">Load Run:</span>
                        <select
                            value={selectedFile}
                            onChange={(e) => setSelectedFile(e.target.value)}
                            className="bg-background border border-border rounded px-2 py-1 text-sm text-text-primary"
                        >
                            {availableFiles.map(f => (
                                <option key={f} value={f}>{f.replace('.json', '')}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </header>

            <div className="visualizer-content" ref={containerRef}>
                {loading && <div className="loading">Loading Tree Data...</div>}
                <svg ref={svgRef}></svg>
            </div>

            <AnimatePresence>
                {selectedNode && (
                    <motion.div
                        className="details-panel"
                        initial={{ x: 400, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        exit={{ x: 400, opacity: 0 }}
                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    >
                        <div className="details-header">
                            <h2>{selectedNode.name}</h2>
                            <button className="close-btn" onClick={() => setSelectedNode(null)}>×</button>
                        </div>

                        <div className="details-body">
                            <div className="badge type">{selectedNode.type}</div>
                            {selectedNode.timestamp && <div className="timestamp">{selectedNode.timestamp}</div>}

                            <h3>Data Payload</h3>
                            <pre className="json-view">
                                {JSON.stringify(selectedNode.details || {}, null, 2)}
                            </pre>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default TreeVisualizer;
